import {
  CatalogueAsset,
  CatalogueCollection,
  CatalogueEntry,
  CatalogueIndex,
  CatalogueRow,
  FilterOption,
  FilterState,
} from './types';

const FORMS: Record<string, string> = {
  equity: 'Stocks',
  fx: 'Spot FX',
  fxcross: 'Spot FX',
  fxforwards: 'FX forwards',
  etf: 'ETFs / ETPs',
  etfequities: 'ETFs / ETPs',
  etfbonds: 'ETFs / ETPs',
  etfcommodities: 'ETFs / ETPs',
  bond: 'Bonds',
  afsbond: 'Bonds',
  htmbond: 'Bonds',
  govtbond: 'Bonds',
  corporatebond: 'Bonds',
  futures: 'Futures',
  crypto: 'Spot crypto',
  metal: 'Spot metal',
  simplifiedoption: 'Simplified options',
  option: 'Options',
  repo: 'Repo',
  cds: 'CDS',
  tbill: 'T-bills',
  yields: 'Yield reference',
};
const EXPOSURES: Record<string, string> = {
  equity: 'Equities',
  fx: 'FX',
  fxcross: 'FX',
  fxforwards: 'FX',
  bond: 'Fixed income',
  afsbond: 'Fixed income',
  htmbond: 'Fixed income',
  govtbond: 'Fixed income',
  corporatebond: 'Fixed income',
  crypto: 'Digital assets',
  metal: 'Commodities',
};

function collectionOptions(collections: CatalogueCollection[]): FilterOption[] {
  return collections.map(collection => ({
    key: `collection:${collection.id}`,
    label:
      collection.label +
      (collection.kind !== 'cluster' && collection.as_of
        ? ` · ${collection.as_of}`
        : ''),
    facet: collection.kind === 'cluster' ? 'collection' : collection.kind,
    collectionIds: [collection.id],
  }));
}

export function buildCatalogue(index: CatalogueIndex, archives = false) {
  const storedDefaults = (index.classification_version || 1) >= 2;
  const termKey = (term: CatalogueIndex['terms'][number]) =>
    storedDefaults ? `term:${term.id}` : `${term.facet}:${term.label}`;
  const terms = new Map(index.terms.map(term => [term.id, term]));
  const collections = new Map(
    index.collections.map(collection => [collection.id, collection]),
  );
  const activeCollection = (id: number) => {
    if (archives) return true;
    const seen = new Set<number>();
    let item = collections.get(id);
    while (item && !seen.has(item.id)) {
      if (!item.active) return false;
      seen.add(item.id);
      item = item.parent_id ? collections.get(item.parent_id) : undefined;
    }
    return true;
  };
  const groupedCollections = collectionOptions(
    index.collections.filter(collection => activeCollection(collection.id)),
  );
  const collectionOptionById = new Map(
    groupedCollections.flatMap(option =>
      option.collectionIds!.map(id => [id, option] as const),
    ),
  );
  const options = new Map<string, FilterOption>();
  const addOption = (
    facet: string,
    label: string,
    key = `${facet}:${label}`,
    collectionIds?: number[],
  ) => {
    options.set(key, {
      ...options.get(key),
      key,
      facet,
      label,
      collectionIds: collectionIds || options.get(key)?.collectionIds,
    });
    return key;
  };
  // Keep even zero-count document categories discoverable.
  index.terms.forEach(term => {
    const key = addOption(term.facet, term.label, termKey(term));
    options.get(key)!.termId = term.id;
  });
  groupedCollections.forEach(option =>
    addOption(option.facet, option.label, option.key, option.collectionIds),
  );
  const rows: CatalogueRow[] = index.assets.map(asset => {
    const facets: Record<string, string[]> = {};
    const origins: NonNullable<CatalogueRow['origins']> = {};
    const add = (
      facet: string,
      label: string,
      key?: string,
      collectionIds?: number[],
    ) => {
      const value = addOption(facet, label, key, collectionIds);
      facets[facet] = [...new Set([...(facets[facet] || []), value])];
    };
    const addTerm = (id: number, origin: 'direct' | 'default') => {
      let term = terms.get(id);
      const seen = new Set<number>();
      while (term && !seen.has(term.id)) {
        seen.add(term.id);
        const key = termKey(term);
        add(term.facet, term.label, key);
        if (origins[key] !== 'direct')
          origins[key] = term.id === id ? origin : 'inherited';
        term = term.parent ? terms.get(term.parent) : undefined;
      }
    };
    asset.terms.forEach(id => addTerm(id, 'direct'));
    // Product implementation defaults are presentation only; they never alter PM class routing.
    if (storedDefaults) {
      const assignedFacets = new Set(
        asset.terms.map(id => terms.get(id)?.facet),
      );
      index.terms.forEach(term => {
        if (
          !assignedFacets.has(term.facet) &&
          !asset.managed_facets?.includes(term.facet) &&
          term.default_for?.includes(asset.runtime_type)
        )
          addTerm(term.id, 'default');
      });
    }
    if (
      !storedDefaults &&
      !facets.form &&
      !asset.managed_facets?.includes('form') &&
      FORMS[asset.runtime_type]
    )
      add('form', FORMS[asset.runtime_type]);
    if (
      !storedDefaults &&
      !facets.exposure &&
      !asset.managed_facets?.includes('exposure') &&
      EXPOSURES[asset.runtime_type]
    )
      add('exposure', EXPOSURES[asset.runtime_type]);
    if (asset.currency && !asset.managed_facets?.includes('currency')) {
      const currency = index.terms.find(
        t => t.code === `currency-${asset.currency.toLowerCase()}`,
      );
      if (storedDefaults && currency) addTerm(currency.id, 'default');
      else add('currency', asset.currency);
    }
    add(
      'availability',
      asset.retired ? 'Retired' : asset.visible ? 'Visible' : 'Hidden',
    );
    let curated = false;
    asset.collections.forEach(id => {
      if (!activeCollection(id)) return;
      let collection = collections.get(id);
      const seen = new Set<number>();
      while (collection && !seen.has(collection.id)) {
        seen.add(collection.id);
        if (
          (collection.active || archives) &&
          (collection.id === id ||
            !asset.managed_collection_kinds?.includes(collection.kind))
        ) {
          curated = true;
          const option = collectionOptionById.get(collection.id)!;
          add(option.facet, option.label, option.key, option.collectionIds);
          if (origins[option.key] !== 'direct')
            origins[option.key] = collection.id === id ? 'direct' : 'inherited';
        }
        // Collection hierarchy remains independent of direct sector assignments.
        collection = collection.parent_id
          ? collections.get(collection.parent_id)
          : undefined;
      }
    });
    return {
      ...asset,
      facets,
      origins,
      curated:
        asset.curation === 'include' ||
        (asset.curation !== 'exclude' && curated),
      searchText: [
        asset.name,
        asset.ticker,
        asset.ric,
        asset.isin,
        asset.primary_role,
        asset.bond_family?.code,
        asset.bond_family ? 'Synthetic bond family' : '',
        ...Object.values(facets)
          .flat()
          .map(key => options.get(key)?.label),
      ]
        .join(' ')
        .toLowerCase(),
    };
  });
  return { rows, options: [...options.values()] };
}

export function matches(
  row: CatalogueRow,
  filters: FilterState,
  query = '',
  curatedOnly = false,
  ignoreFacet?: string,
) {
  if (curatedOnly && !row.curated) return false;
  if (
    query.trim() &&
    !query
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .every(word => row.searchText.includes(word))
  )
    return false;
  return Object.entries(filters).every(
    ([facet, selected]) =>
      facet === ignoreFacet ||
      !selected.length ||
      selected.some(key => row.facets[facet]?.includes(key)),
  );
}

export function facetCounts(
  rows: CatalogueRow[],
  filters: FilterState,
  query: string,
  curatedOnly: boolean,
) {
  const result: Record<string, Set<string>> = {};
  rows.forEach(row => {
    Object.entries(row.facets).forEach(([facet, keys]) => {
      if (matches(row, filters, query, curatedOnly, facet))
        keys.forEach(key => {
          (result[key] ||= new Set()).add(catalogueKey(row));
        });
    });
  });
  return Object.fromEntries(
    Object.entries(result).map(([key, ids]) => [key, ids.size]),
  );
}

export function addSelected(selected: number[], added: number[]) {
  return [...new Set([...selected, ...added])];
}
export function removeSelected(selected: number[], removed: number[]) {
  const remove = new Set(removed);
  return selected.filter(id => !remove.has(id));
}

export function catalogueKey(
  row: Pick<CatalogueAsset, 'id' | 'runtime_type' | 'bond_family'>,
) {
  return row.bond_family && row.runtime_type === 'afsbond'
    ? `family:${row.bond_family.id}`
    : `asset:${row.id}`;
}

// The universe count follows the complete live selection, never table filters.
// Unresolved IDs still count individually; family identity is never guessed.
export function selectedInstrumentCount(
  selected: readonly number[],
  assets: readonly Pick<
    CatalogueAsset,
    'id' | 'runtime_type' | 'bond_family'
  >[],
) {
  const keys = new Map(assets.map(asset => [asset.id, catalogueKey(asset)]));
  return new Set(selected.map(id => keys.get(id) ?? `asset:${id}`)).size;
}

// Call AFTER matching individual rows: tags on different issues must never
// combine to satisfy an intersection that no concrete instrument satisfies.
export function catalogueEntries(rows: CatalogueRow[]): CatalogueEntry[] {
  const entries = new Map<string, CatalogueEntry>();
  rows.forEach(row => {
    const key = catalogueKey(row);
    const existing = entries.get(key);
    if (existing) existing.members.push(row);
    else
      entries.set(key, {
        key,
        family: key.startsWith('family:') ? row.bond_family! : null,
        members: [row],
      });
  });
  return [...entries.values()];
}
